import type { ProfitLossSnapshot } from "../../shared/types";
import { mockProfitLoss } from "../mocks/profitLoss";
import { getRequired, getWithFallback } from "./apiClient";

export interface ProfitLossQueryFilters {
  month?: number;
  station?: string;
  year?: number;
}

export interface ProfitLossStationOption {
  label: string;
  shortName: string;
  value: string;
}

export type ProfitLossSnapshotView = ProfitLossSnapshot & {
  filters?: {
    available: {
      months: number[];
      stations: ProfitLossStationOption[];
      years: number[];
    };
    selected: {
      month?: number;
      station?: string;
      stationLabel: string;
      year: number;
    };
  };
};

const profitLossCacheTtlMs = 10 * 60 * 1000;
const cachedProfitLoss = new Map<string, { expiresAt: number; snapshot: ProfitLossSnapshotView }>();
const inFlightProfitLoss = new Map<string, Promise<ProfitLossSnapshotView>>();

function cacheKey(filters: ProfitLossQueryFilters = {}): string {
  return [
    `station=${filters.station || "all"}`,
    `year=${filters.year || "latest"}`,
    `month=${filters.month || "ytd"}`
  ].join("&");
}

function endpoint(filters: ProfitLossQueryFilters = {}): string {
  const params = new URLSearchParams();
  if (filters.station && filters.station !== "all") {
    params.set("station", filters.station);
  }
  if (filters.year) {
    params.set("year", String(filters.year));
  }
  if (filters.month) {
    params.set("month", String(filters.month));
  }

  const query = params.toString();
  return query ? `/finance/profit-loss?${query}` : "/finance/profit-loss";
}

export function readCachedProfitLoss(filters: ProfitLossQueryFilters = {}): ProfitLossSnapshotView | undefined {
  const cached = cachedProfitLoss.get(cacheKey(filters));
  if (!cached || cached.expiresAt <= Date.now()) {
    return undefined;
  }

  return cached.snapshot;
}

export function getProfitLoss(options: { filters?: ProfitLossQueryFilters; forceRefresh?: boolean; realDataOnly?: boolean } = {}): Promise<ProfitLossSnapshotView> {
  if (options.realDataOnly) {
    return getRequired<ProfitLossSnapshotView>(endpoint(options.filters));
  }

  const key = cacheKey(options.filters);
  const cached = readCachedProfitLoss(options.filters);
  if (!options.forceRefresh && cached) {
    return Promise.resolve(cached);
  }

  const inFlight = inFlightProfitLoss.get(key);
  if (inFlight) {
    return inFlight;
  }

  const request = getWithFallback<ProfitLossSnapshotView>(endpoint(options.filters), () => mockProfitLoss)
    .then((snapshot) => {
      cachedProfitLoss.set(key, {
        expiresAt: Date.now() + profitLossCacheTtlMs,
        snapshot
      });
      return snapshot;
    })
    .finally(() => {
      inFlightProfitLoss.delete(key);
    });

  inFlightProfitLoss.set(key, request);
  return request;
}
